import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, Button, Dialog, DialogContent, Tooltip } from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridRowHeightParams,
  GridToolbarContainer,
  GridToolbarContainerProps,
} from '@mui/x-data-grid-premium';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import { Spinner } from '@/components/common/Spinner';
import useAttachment from '@/hooks/useAttachment';
import { AttachmentProps, FileType } from '@/models/Attachment';

import ImagePreview from './ImagePreview';
import { lythoCollectionUrl } from '@/utils/constants';
import { GridLocaleTextOverride } from '@/utils/dataGridUtil';
import { LocaleContext } from '@/Contexts/LocaleContext';
export default function ManageAttachmentsModal({
  applicationId,
  onClose,
}: {
  applicationId: number;
  onClose: () => void;
}) {
  const { locale } = useContext(LocaleContext);

  const { getApplicationAttachments, uploadAttachmentToLytho } = useAttachment(); // Replace with actual props or state
  const hasFetchedRef = useRef(false);

  const [attachments, setAttachments] = useState<AttachmentProps[] | undefined>(undefined);
  const [savingIds, setSavingIds] = useState<Set<number | string>>(new Set());

  const handleClose = () => {
    onClose();
  };

  const lythoActionButton = useCallback(
    (title: string, icon: React.ReactNode, disabled: boolean, onClick?: () => void) => {
      return (
        <Box height={'100%'} display={'flex'} justifyContent='center' alignItems='center'>
          <Tooltip title={title}>
            <span>
              <Button variant='secondary' disabled={disabled} onClick={onClick}>
                {icon}
              </Button>
            </span>
          </Tooltip>
        </Box>
      );
    },
    []
  );

  const uploadToLytho = useCallback(
    async (attachmentId: number) => {
      if (uploadAttachmentToLytho == null) return;
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.add(attachmentId);
        return next;
      });

      const lythoLink = await uploadAttachmentToLytho(attachmentId);
      if (lythoLink == null) return;
      // update the attachments state to reflect the new lythoLink
      setAttachments((prevAttachments) => {
        if (prevAttachments === undefined) return prevAttachments;
        return prevAttachments.map((attachment) => {
          if (attachment.id === attachmentId) {
            return {
              ...attachment,
              lythoLink: lythoLink, // Placeholder, ideally fetch the actual link
            };
          }
          return attachment;
        });
      });

      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(attachmentId);
        return next;
      });
    },
    [uploadAttachmentToLytho]
  );

  const GridToolBarCustom = useCallback((props: GridToolbarContainerProps) => {
    return (
      <GridToolbarContainer {...props}>
        <Button
          variant='secondary'
          size='small'
          onClick={() => {
            window.open(lythoCollectionUrl != null ? lythoCollectionUrl : '', '_blank');
          }}
        >
          View Lytho Collection
        </Button>
      </GridToolbarContainer>
    );
  }, []);

  //make sure lythoLink is a valid url
  const isValidUrl = (urlString: string) => {
    try {
      new URL(urlString);
      return true;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchAttachments = async () => {
      const data = await getApplicationAttachments(applicationId);
      setAttachments(data ?? []);
    };

    void fetchAttachments();
  }, [getApplicationAttachments, applicationId]);

  const columns: GridColDef[] = [
    {
      field: 'thumbnail',
      headerName: 'Thumbnail',
      minWidth: 80, // Adjusted from flex
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'data-grid-header-green',
      renderCell: (params) => {
        const row = params.row as AttachmentProps;
        return (
          row.fileType == FileType.image &&
          row.uri != null && <ImagePreview src={row.uri} alt={row.fileName ?? 'Image'} />
        );
      },
    },
    {
      field: 'sendToLythoBtn',
      headerName: 'Lytho Action',
      minWidth: 20, // Adjusted from flex
      flex: 0.5,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'data-grid-header-green',
      renderCell: (params) => {
        const attachment = params.row as AttachmentProps;

        if (attachment.fileType != FileType.image) {
          return lythoActionButton(
            'Urls cannot be uploaded to Lytho',
            <FileUploadOutlinedIcon />,
            true
          );
        }

        const isUploading = attachment.id != null && savingIds.has(attachment.id);
        if (isUploading) {
          return (
            <Box display={'flex'} justifyContent='center' alignItems='center' height={'100%'}>
              <Spinner type='circular' size={20} />
            </Box>
          );
        }

        if (attachment.lythoLink == null && attachment.id != null) {
          return lythoActionButton('Upload to Lytho', <FileUploadOutlinedIcon />, false, () =>
            uploadToLytho(attachment.id!)
          );
        } else if (attachment.lythoLink != null && isValidUrl(attachment.lythoLink)) {
          return lythoActionButton('View in Lytho', <VisibilityOutlinedIcon />, false, () =>
            window.open(attachment.lythoLink!, '_blank')
          );
        }

        return <></>;
      },
    },
    {
      field: 'questionId',
      headerName: 'Question',
      minWidth: 40, // Adjusted from flex
      flex: 1.5,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'data-grid-header-green',
      renderCell: (params) => {
        const row = params.row as AttachmentProps;
        // I need a tooltip that shows the full question text on hover
        return row.questionText != null ? (
          <Tooltip title={row.questionText}>
            <span>{row.questionText}</span>
          </Tooltip>
        ) : (
          <span>N/A</span>
        );
      },
    },
    {
      field: 'fileName',
      headerName: 'File Name',
      minWidth: 40, // Adjusted from flex
      flex: 2,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'data-grid-header-green',
    },
  ];

  return (
    <Dialog open={true} onClose={() => handleClose()} maxWidth={false}>
      <DialogTitleWithClose
        onClose={handleClose}
      >{`Manage Attachments for AppID: ${applicationId}`}</DialogTitleWithClose>
      <DialogContent>
        <Box display='flex' flexDirection='column' width={'80vw'} height={'60vh'}>
          {attachments === undefined ? (
            <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
              <Spinner type='circular' />
            </Box>
          ) : (
            <DataGridPremium
              showToolbar
              localeText={GridLocaleTextOverride(locale)}
              getRowId={(row: AttachmentProps) =>
                `${row?.questionId?.toString() ?? ''}${row.createdOn != null ? row.createdOn.toString() : ''}`
              }
              getRowHeight={(params: GridRowHeightParams) =>
                params.model.fileType == FileType.image ? 100 : 40
              }
              pagination
              autoPageSize
              rows={attachments}
              columns={columns}
              disableColumnResize // Optional: Disable column resizing
              slots={{
                toolbar: GridToolBarCustom,
              }}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
